import { DoMoreProps } from 'util/interfaces'
import globalConfig from 'init/global.config'
import globalStore from 'init/global-store'
import { hostsConfig } from 'init/config'
import msg from 'services/msg'
import ssoService from 'services/sso-service'
import submissionLoader from 'widgets/submission/submission-loader'
import submissionStore, { QaLaunchData, SubmissionPage, SubmissionPageName }
  from 'store/submission-store'
import tokenService from 'services/token-service'
import { TOKEN_PARAM_NAME, TURNTO_EMAIL_TYPE, TurnToFlow, TURNTOSKU_PARAM }
  from './email-flows-common'
import waitForValue from 'util/wait-for-value'

export default {
  process(
    flow: TurnToFlow,
    params: URLSearchParams
  ): Promise<boolean> {
    return new Promise((resolve) => {
      if (!isQaFlow(flow, params)) {
        resolve(false)
        return
      }

      if (flow === 'irrelevantq' || flow === 'optout') {
        import(/* webpackChunkName: 'standalone-popover' */ 'widgets/standalone-popover/standalone-popover-loader').then((popoverLoader) => {
          popoverLoader.default.load({
            message: msg(`flowMsg.${flow === 'optout' ? `${flow}.question` : flow}`)
          })
        })
        resolve(true)
        return
      }

      // All content solicitation cases should have a token. The "question approval email" sent to
      // moderators is posing as a content soliciation email with turntoflow=answerview (TT-11589).
      // This will skip it as it doesn't have a token.
      if (!params.get(TOKEN_PARAM_NAME)) {
        resolve(false)
        return
      }

      const qaLaunchData = {} as QaLaunchData
      let page = 'solo-question' as SubmissionPageName
      let props = {}
      qaLaunchData.emailToken = params.get(TOKEN_PARAM_NAME)
      // coming from content solicitation email, need to remove currently logged in user (if any)
      tokenService.logoutUser()

      if (flow === 'answer') {
        qaLaunchData.view = 'answer'
        qaLaunchData.answerText = params.get('ttAnswer')
        // itemQuestionRecipientLogId
        const iqrlId = params.get('iqrlId')
        qaLaunchData.iqrlId = iqrlId ? parseInt(iqrlId) : null
      } else if (flow === 'thankanswer') {
        qaLaunchData.view = 'replyToAnswer'
      } else if (flow === 'answerreply') {
        qaLaunchData.view = 'replyToReply'
      } else if (flow === 'answerview' || flow === 'replyview') {
        qaLaunchData.view = 'seeAll'
      } else if (flow === 'thanka') {
        page = 'do-more'
        props = { msgType: 'thank' } as DoMoreProps
      }

      waitForValue(() => params.get(TURNTOSKU_PARAM) || globalStore.sku).then((sku: string) => {
        if (globalConfig.ssoEnabled) {
          ssoService.ssoGetLoggedInData(sku, { page, props }, qaLaunchData, 'qa')
          resolve(true)
          return
        }
        submissionStore.startQaFlow(
          sku,
          hostsConfig.siteKey,
          { page, props } as SubmissionPage,
          qaLaunchData
        )
        submissionLoader.load().then(() => resolve(true)).catch(() => {})
      }).catch(() => {})
    })
  }
}

function isQaFlow(flow: TurnToFlow, params: URLSearchParams) {
  // If flow is 'optout', need to make sure it is from a question email, not an RSE
  return (flow === 'optout' && params.get(TURNTO_EMAIL_TYPE) === 'question') ||
    [
      'answer', // clicking "answer this question" in question email
      'irrelevantq', // clicking not apporpriate link in question email
      'thankanswer', // clicking "reply" from an answer email
      'thanka', // clicking "thank" from an answer email
      'answerview', // clicking on "see all answers" from answer email
      'answerreply', // clicking "reply" from reply email
      'replyview' // clicking on "see all answers" from reply email
    ].indexOf(flow) > -1
}
